import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { COOKIE_CONSENT_VERSION } from '@/content/legal/types';
import { updateGA4Consent } from '@/monitoring/ga4';

const STORAGE_KEY = 'auw_cookie_consent';

export interface CookieCategories {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

interface StoredConsent {
  version: string;
  agreed_at: string;
  categories: CookieCategories;
}

const DEFAULT_CATEGORIES: CookieCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
};

/* ── snapshot cache (useSyncExternalStore requires referential stability) ── */

let cachedRaw: string | null = null;
let cachedSnapshot: StoredConsent | null = null;

function readStored(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (!parsed.categories) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getSnapshot(): StoredConsent | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = raw ? readStored() : null;
  return cachedSnapshot;
}

/* ── listeners ── */

let listeners: Array<() => void> = [];

function emitChange(): void {
  cachedRaw = localStorage.getItem(STORAGE_KEY);
  cachedSnapshot = cachedRaw ? readStored() : null;
  for (const fn of listeners) fn();
}

function writeStored(consent: StoredConsent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // storage access blocked
  }
  emitChange();
}

function subscribe(cb: () => void): () => void {
  listeners.push(cb);
  return () => {
    listeners = listeners.filter((fn) => fn !== cb);
  };
}

function buildStoredConsent(categories: CookieCategories): StoredConsent {
  return {
    version: COOKIE_CONSENT_VERSION,
    agreed_at: new Date().toISOString(),
    categories,
  };
}

function syncGA4(categories: CookieCategories): void {
  updateGA4Consent({
    analytics: categories.analytics,
    marketing: categories.marketing,
  });
}

export function useCookieConsent() {
  const stored = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const hasConsented = useCallback((): boolean => {
    const s = stored ?? readStored();
    return s?.version === COOKIE_CONSENT_VERSION;
  }, [stored]);

  const categories = useMemo<CookieCategories>(() => {
    const s = stored ?? readStored();
    if (s?.version === COOKIE_CONSENT_VERSION && s.categories) {
      return { ...s.categories, necessary: true };
    }
    return { ...DEFAULT_CATEGORIES };
  }, [stored]);

  const acceptAll = useCallback((): void => {
    const consent = buildStoredConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
    writeStored(consent);
    syncGA4(consent.categories);
  }, []);

  const acceptSelected = useCallback((selected: Partial<CookieCategories>): void => {
    const cats: CookieCategories = {
      necessary: true,
      analytics: selected.analytics ?? false,
      marketing: selected.marketing ?? false,
    };
    const consent = buildStoredConsent(cats);
    writeStored(consent);
    syncGA4(cats);
  }, []);

  const rejectOptional = useCallback((): void => {
    const consent = buildStoredConsent({ ...DEFAULT_CATEGORIES });
    writeStored(consent);
    syncGA4(consent.categories);
  }, []);

  return { hasConsented, categories, acceptAll, acceptSelected, rejectOptional };
}

/**
 * 앱 초기화 시 호출: 이전에 저장된 동의 상태를 GA4 Consent Mode에 반영한다.
 */
export function restoreCookieConsentToGA4(): void {
  const stored = readStored();
  if (
    stored?.version === COOKIE_CONSENT_VERSION
    && stored.categories
    && stored.categories.analytics
  ) {
    syncGA4(stored.categories);
  }
}
